<template>
	<div class="notification-container" @click="toggleNotifications">
		<span class="material-icons-outlined notification p-relative">
			notifications
			<span v-if="unreadCount > 0" class="badge">{{ unreadCount }}</span>
		</span>
		<transition name="notifications-dropdown">
			<div v-show="showNotifications" class="notifications-dropdown">
				<div v-if="loading" class="loading">{{ $t("Custom.loading") }}...</div>
				<div v-else>
					<div v-if="notifications.length === 0" class="no-notifications">
						{{ $t("Notification.noNotifications") }}
					</div>

					<div v-else class="notification-list">
						<div class="modal-header">
							<div class="modal-title-container">
								<div class="modal-divider"></div>
							</div>
						</div>
						<div class="notifications-header">
							<h3>{{ $t("Notification.notifications") }}</h3>
							<router-link
								to="/notifications"
								class="show-all"
								@click="showNotifications = false"
							>
								{{ $t("Notification.showAll") }}
							</router-link>
						</div>
						<NotificationItem
							v-for="notification in notifications"
							:key="notification.id"
							:notification="notification"
							:isActionTags="isActionTags(notification)"
							@clickItem="clickItem(notification)"
						/>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import apiClient from "@/axios";
import PlatformTypeEnum from "@/enums/PlatformTypeEnum";
import NotificationItem from "@/components/custom/items/NotificationItem.vue";
import { mapActions } from "vuex";
import {
	getNotifications,
	markNotificationAsRead,
} from "@/api/notification.api";
import { detectPlatform, extractStoreId } from "@/ultis/user/functions/storePlatform";

export default {
	name: "NotificationItemComponent",
	components: {
		NotificationItem,
	},
	data() {
		return {
			showNotifications: false,
			notifications: [],
			loading: false,
			unreadCount: 0,
			originalScrollPosition: 0,
		};
	},
	methods: {
		...mapActions("pageTitle", ["updatePageTitle"]),
		async fetchNotifications() {
			try {
				this.loading = true;
				const response = await getNotifications();
				if (response.data.status) {
					this.notifications = response.data.data;
					this.updateUnreadCount();
				} else {
					console.error(response.data.msg);
				}
			} catch (error) {
				console.error(error);
			} finally {
				this.loading = false;
			}
		},
		async clickItem(notification) {
			const url = notification?.data?.addtionalData?.url;
      const platform = detectPlatform(url);
      const storeId = notification?.data?.addtionalData?.storeId;
      const appStoreId = storeId ? storeId : extractStoreId(url, platform);

      if (!notification?.read_at) {
        await markNotificationAsRead(notification.id);
      }
      const type = notification?.data?.type;
      const query = {};

      if (type === "app-update") {
        query.country = notification?.data?.addtionalData?.country?.countryShortName || "US";
        query.historyId = notification?.data?.addtionalData?.historyId;
      }
      switch (type) {
        case "app-update":
          this.$router.push({
            name: "App",
            params: { storeId: appStoreId },
            query,
            }).then(() => window.location.reload());
          break;
        case "add-app-to-developer":
          this.$router.push({
            name: "App",
            params: { storeId: appStoreId },
              query: {
                country: query.country,
              }
            }).then(() => window.location.reload());
          break;
        case "added-developer-list-access":
          this.$router.push({ name: "DeveloperLists" 
            }).then(() => window.location.reload());
          break;
        case "added-app-list-access":
          this.$router.push({ name: "AppLists" 
            }).then(() => window.location.reload());
          break;
      }
    },
    async handleMarkAsRead(notificationId) {
      try {
        await markNotificationAsRead(notificationId);
      } catch (error) {
        console.error(
          "An error occurred while marking notification as read:",
          error
        );
      }
    },
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      if (this.showNotifications && this.notifications.length === 0) {
        this.fetchNotifications();
      }
      if (window.innerWidth <= 768) {
        if (this.showNotifications) {
          this.originalScrollPosition = window.scrollY; // Orijinal konumu kaydet
          document.body.style.position = "fixed";
          document.body.style.top = `-${this.originalScrollPosition}px`;
          document.body.style.width = "100%";
        } else {
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.width = "";
          window.scrollTo(0, this.originalScrollPosition); // Orijinal konuma geri dön
        }
      }
    },
    updateUnreadCount() {
      this.unreadCount = this.notifications.filter((n) => !n.seen).length;
    },
    isActionTags(notification) {
      if (notification.data.type === "app-update") return true;
      return false;
    },
    formatDate(date) {
      if (!date) return "";
      const locale = localStorage.getItem("localeTime") || "en-US";
      return new Date(date).toLocaleDateString(locale);
    },
    storeImage(platform) {
      platform = PlatformTypeEnum.fromId(platform);
      let imageName = "";
      switch (platform) {
        case "Play Store":
          imageName = "playStore.png";
          break;
        case "App Store":
          imageName = "appStore.png";
          break;
        default:
          imageName = "appStore.png";
          break;
      }
      return require(`@/assets/img/stores/${imageName}`);
    },
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.showNotifications = false;
        if (window.innerWidth <= 768) {
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.width = "";
          window.scrollTo(0, this.originalScrollPosition);
        }
      }
    },
	},
	mounted() {
		this.fetchNotifications();
		document.addEventListener("click", this.handleClickOutside);
	},
	beforeUnmount() {
		document.removeEventListener("click", this.handleClickOutside);
		if (window.innerWidth <= 768) {
			document.body.style.position = "";
			document.body.style.top = "";
			document.body.style.width = "";
			window.scrollTo(0, this.originalScrollPosition);
		}
	},
};
</script>

<style scoped>
.notification-container {
	position: relative;
	text-decoration: none;
	color: #000;
	display: flex;
	align-items: center;
	background-color: #f4f5fa;
	padding: 5px;
	margin-right: 8px;
	border-radius: 50%;
	cursor: pointer;
}

.notifications-dropdown {
	position: absolute;
	top: 115%;
	right: 0;
	border-radius: 10px;
	background: white;
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
	width: 350px;
	max-height: 70vh;
	overflow-y: auto;
	z-index: 10;
}

.notifications-header {
	padding: 15px;
	border-bottom: 1px solid #eee;
	display: flex;
	justify-content: space-between;
	align-items: center;
	position: sticky;
	top: 0;
	background: white;
	border-radius: 10px 10px 0 0;
	z-index: 1;
}

.notifications-header h3 {
	margin: 0;
	font-size: 16px;
	font-weight: 600;
}

.show-all {
	color: #2196f3;
	text-decoration: none;
	font-size: 14px;
	font-weight: 500;
}

.show-all:hover {
	text-decoration: underline;
}

.notification-item {
	display: flex;
	align-items: center;
	padding: 10px;
	margin: 5px 5px;
	border-radius: 8px;
	cursor: pointer;
	position: relative;
}

.notification-item:last-child {
	border-bottom: none;
}

.notification-image {
	width: 40px;
	height: 40px;
	margin-right: 10px;
	border-radius: 15%;
}

.notification-details {
	flex: 1;
	display: flex;
	flex-direction: column;
}

.notification-title {
	font-weight: bold;
	font-size: 0.9rem;
}

.notification-message {
	font-size: 12px;
	color: #555;
	margin-bottom: 5px;
}

.created-at {
	font-size: 12px;
	color: #999;
}

.store-icon {
	width: 15px;
	height: 15px;
	position: absolute;
	top: 4px;
	right: 12px;
}

.loading {
	padding: 20px;
	text-align: center;
}

.no-notifications {
	padding: 20px;
	text-align: center;
	color: #777;
}

.unread {
	background-color: white;
}

.unread:hover {
	background-color: #f4f5fa;
}

.badge {
	background-color: red;
	color: white;
	border-radius: 50%;
	width: 8px;
	height: 7px;
	font-size: 5px;
	position: absolute;
	top: 0;
	right: 0;
	transform: translate(50%, -50%);
}

.unread-dot {
	position: absolute;
	right: 2px;
	top: 10%;
	transform: translateY(-50%);
	width: 8px;
	height: 8px;
	background-color: red;
	border-radius: 50%;
}

.modal-header {
	display: none;
}

.modal-title-container {
	display: flex;
	justify-content: center;
}

.modal-divider {
	width: 70px;
	height: 4px;
	background: #ccc;
	border-radius: 2px;
	margin-top: 8px;
	cursor: pointer;
	display: none; /* Initially hidden */
}

.notifications-dropdown-enter-active,
.notifications-dropdown-leave-active {
	transition: transform 0.3s ease, opacity 0.3s ease;
}
.notifications-dropdown-enter,
.notifications-dropdown-leave-to {
	opacity: 0;
	transform: translateY(-10px);
}

@media screen and (max-width: 768px) {
	.notifications-dropdown {
		position: fixed;
		bottom: 0;
		top: 50%;
		left: 0;
		width: 100%;
		max-height: 80vh;
		border-radius: 10px 10px 0 0;
		box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
		animation: slideUp 0.3s ease-in-out;
	}

	.modal-header {
		display: flex;
		flex-direction: column;
		padding: 4px 10px 0px 9px;
	}

	.modal-divider {
		display: block;
	}

	.notifications-dropdown-enter-active,
	.notifications-dropdown-leave-active {
		transition: transform 0.3s ease, opacity 0.3s ease;
	}
	.notifications-dropdown-enter,
	.notifications-dropdown-leave-to {
		transform: translateY(100%);
		opacity: 0;
	}
	.store-icon {
		top: 4px;
		right: 27px;
	}

	.unread-dot {
		right: 12px;
		top: 14%;
	}
	.material-icons-outlined {
		font-size: 18px;
	}

	@keyframes slideUp {
		from {
			transform: translateY(100%);
		}
		to {
			transform: translateY(0);
		}
	}
}
</style>
